/**
 * Modèle des boutons de la plateforme : UN identifiant choisi par l'exploitant
 * dans une liste fermée, traduit ici en valeurs des variables `--btn-*` que lit
 * `assets/main.css`.
 *
 * Fonctions PURES, comme `palette.ts` — l'application au document vit dans
 * `appliquer.ts`. Aucune valeur reçue n'entre dans la feuille : seul
 * l'identifiant voyage, les valeurs sont écrites dans ce fichier.
 *
 * Les valeurs ne nomment que des jetons (`var(--color-…)`), jamais une couleur :
 * déclarées sur l'élément `.btn`, elles se résolvent sur le bouton, et suivent
 * donc le thème, les couleurs réglées et l'aperçu de l'écran d'apparence.
 */

/**
 * Modèles proposés. MIROIR de `STYLES_BOUTONS` (backend
 * `core/apparence/schemas.ts`) et de la migration 0048 — un test backend fige
 * les trois.
 */
export const STYLES_BOUTONS = ['plat', 'relief', 'doux'] as const
export type StyleBoutons = (typeof STYLES_BOUTONS)[number]

export interface BoutonsPlateforme {
  style?: StyleBoutons
}

/** Le modèle de `main.css` : celui qu'on obtient sans aucune variable déclarée. */
export const DEFAUT_STYLE_BOUTONS: StyleBoutons = 'plat'

/**
 * Toutes les variables que lit `main.css`, avec leur valeur ADS en repli. Un
 * test vérifie que la liste et la feuille se correspondent exactement : une
 * variable écrite ici sans être lue ne ferait rien, une variable lue sans être
 * listée ne serait jamais remise à zéro dans l'aperçu.
 */
export const VARIABLES_BOUTONS = [
  '--btn-bg',
  '--btn-border',
  '--btn-text',
  '--btn-shadow',
  '--btn-bg-hover',
  '--btn-border-hover',
  '--btn-text-hover',
  '--btn-shadow-hover',
  '--btn-bg-active',
  '--btn-press',
  '--btn-primary-bg',
  '--btn-primary-text',
  '--btn-primary-shadow',
  '--btn-primary-bg-hover',
  '--btn-primary-shadow-hover',
  '--btn-primary-bg-active',
  '--btn-danger-bg',
  '--btn-danger-text',
  '--btn-danger-shadow',
  '--btn-danger-bg-hover',
  '--btn-danger-shadow-hover',
  '--btn-danger-bg-active',
] as const
export type VariableBouton = (typeof VARIABLES_BOUTONS)[number]

/** Dégradé vertical d'une couleur : éclaircie en haut, pleine en bas. */
const degrade = (jeton: string) =>
  `linear-gradient(180deg, color-mix(in srgb, var(${jeton}) 82%, white), var(${jeton}))`

/**
 * Reflet du bord supérieur. Mêlé à la surface plutôt qu'en blanc fixe : un
 * blanc opaque se verrait comme un trait sur un bouton sombre.
 */
const REFLET_SURFACE = 'inset 0 1px 0 color-mix(in srgb, var(--color-surface) 70%, white)'
const REFLET_PLEIN = 'inset 0 1px 0 rgb(255 255 255 / 0.22)'

/** Halo coloré du survol en relief, à partir de la couleur du bouton. */
const halo = (jeton: string) => `0 3px 8px color-mix(in srgb, var(${jeton}) 35%, transparent)`

/** Fond doux, foncé vers sa couleur de base pour le survol et l'appui. */
const vers = (base: string, fond: string, part: number) =>
  `color-mix(in srgb, var(${base}) ${part}%, var(${fond}))`

/**
 * Valeurs de chaque modèle. « plat » est VIDE à dessein : c'est `main.css` tel
 * quel, et le répéter ici ferait deux sources à garder identiques.
 *
 * - relief : dégradé, ombre portée, reflet, enfoncement d'un pixel à l'appui ;
 * - doux   : fonds teintés de la couleur, texte coloré, sans bordure.
 */
export const MODELES_BOUTONS: Readonly<
  Record<StyleBoutons, Readonly<Partial<Record<VariableBouton, string>>>>
> = {
  plat: {},
  relief: {
    '--btn-bg': 'linear-gradient(180deg, var(--color-surface), var(--color-surface-hover))',
    '--btn-shadow': `0 1px 2px rgb(0 0 0 / 0.18), ${REFLET_SURFACE}`,
    '--btn-bg-hover':
      'linear-gradient(180deg, var(--color-surface-hover), var(--color-surface-pressed))',
    '--btn-shadow-hover': `0 2px 4px rgb(0 0 0 / 0.2), ${REFLET_SURFACE}`,
    '--btn-press': 'translateY(1px)',
    '--btn-primary-bg': degrade('--color-primary'),
    '--btn-primary-shadow': `0 1px 2px rgb(0 0 0 / 0.25), ${REFLET_PLEIN}`,
    '--btn-primary-bg-hover': degrade('--color-primary-hover'),
    '--btn-primary-shadow-hover': `${halo('--color-primary')}, ${REFLET_PLEIN}`,
    '--btn-danger-bg': degrade('--color-danger'),
    '--btn-danger-shadow': `0 1px 2px rgb(0 0 0 / 0.25), ${REFLET_PLEIN}`,
    '--btn-danger-bg-hover': degrade('--color-danger-text'),
    '--btn-danger-shadow-hover': `${halo('--color-danger')}, ${REFLET_PLEIN}`,
  },
  doux: {
    '--btn-bg': 'var(--color-surface-hover)',
    '--btn-border': 'transparent',
    '--btn-text': 'var(--color-text)',
    '--btn-bg-hover': 'var(--color-surface-pressed)',
    '--btn-border-hover': 'transparent',
    '--btn-bg-active': vers('--color-text', '--color-surface-pressed', 10),
    '--btn-primary-bg': 'var(--color-primary-soft)',
    '--btn-primary-text': 'var(--color-primary-text)',
    '--btn-primary-bg-hover': vers('--color-primary', '--color-primary-soft', 16),
    '--btn-primary-bg-active': vers('--color-primary', '--color-primary-soft', 28),
    '--btn-danger-bg': 'var(--color-danger-soft)',
    '--btn-danger-text': 'var(--color-danger-text)',
    '--btn-danger-bg-hover': vers('--color-danger', '--color-danger-soft', 16),
    '--btn-danger-bg-active': vers('--color-danger', '--color-danger-soft', 28),
  },
}

export function estStyleBoutons(valeur: unknown): valeur is StyleBoutons {
  return typeof valeur === 'string' && (STYLES_BOUTONS as readonly string[]).includes(valeur)
}

/** Valide une forme reçue et la recopie, ou `null` si UN élément est hors contrat. */
export function normaliserBoutons(brut: unknown): BoutonsPlateforme | null {
  if (typeof brut !== 'object' || brut === null || Array.isArray(brut)) return null
  const resultat: BoutonsPlateforme = {}
  for (const [cle, valeur] of Object.entries(brut)) {
    if (cle !== 'style' || !estStyleBoutons(valeur)) return null
    resultat.style = valeur
  }
  return resultat
}

/**
 * Variables d'un modèle, TOUTES présentes : celles qu'il ne règle pas valent
 * `initial`, qui fait retomber `var(--btn-…, repli)` sur la valeur de
 * `main.css`. Pour l'aperçu, posé en ligne sur ses boutons : il doit montrer le
 * modèle du brouillon même quand le document en applique un autre — un
 * « plat » qui ne déclarerait rien hériterait du modèle enregistré.
 */
export function jetonsBoutons(style: StyleBoutons): Record<VariableBouton, string> {
  if (!estStyleBoutons(style)) throw new Error(`Modèle de boutons inconnu : ${JSON.stringify(style)}`)
  const modele = MODELES_BOUTONS[style]
  return Object.fromEntries(
    VARIABLES_BOUTONS.map((nom) => [nom, modele[nom] ?? 'initial']),
  ) as Record<VariableBouton, string>
}

/**
 * Déclarées sur `.btn` lui-même, et non sur `:root` : une variable déclarée sur
 * la racine y résoudrait ses `var(--color-…)` une fois pour toutes, et un
 * bouton placé sous des jetons redéfinis (aperçu, contraste) garderait les
 * couleurs de la racine. Spécificité (0,3,1) : seule cette feuille et l'aperçu
 * (en ligne) déclarent ces variables. Le contraste renforcé est exclu, comme
 * pour les couleurs : il garde le modèle de `main.css`, le plus lisible.
 */
export const SELECTEUR_BOUTONS = "html:root:not([data-contrast='high']) .btn"

/** Feuille du modèle, `''` pour le modèle par défaut. Lève sur un modèle inconnu. */
export function feuilleBoutons(boutons: BoutonsPlateforme): string {
  const style = boutons.style
  if (style === undefined) return ''
  if (!estStyleBoutons(style)) throw new Error(`Modèle de boutons inconnu : ${JSON.stringify(style)}`)
  const lignes = Object.entries(MODELES_BOUTONS[style]).map(([n, v]) => `  ${n}: ${v};`)
  return lignes.length === 0 ? '' : `${SELECTEUR_BOUTONS} {\n${lignes.join('\n')}\n}`
}
